import React, { useState, useEffect } from "react";
import { getApps } from "../services/api";
import { Link } from "react-router-dom";
import { ArrowRight, Search, Layers } from "lucide-react";
import Logo from "../assets/quivix.png";

const getAppImage = (app) =>
  app?.image || app?.icon_url || app?.cover_image || Logo;

const AppsPage = () => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    document.title = "Applications Suite — QuivixDigital";
    getApps()
      .then((res) => setApps(Array.isArray(res) ? res : res.data || []))
      .catch((err) => console.error("Failed to load apps:", err))
      .finally(() => setLoading(false));
  }, []);

  const filteredApps = apps.filter((app) => {
    const matchesSearch = app.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.description?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  return (
    <div className="bg-black text-[#f5f5f7] min-h-screen pt-36 pb-24 font-sans selection:bg-blue-500/30">
      <div className="max-w-6xl mx-auto px-6 sm:px-8">
        
        {/* Header Title */}
        <div className="max-w-3xl mb-14">
          <span className="text-xs font-mono uppercase tracking-widest text-blue-400">QuivixDigital Software Suite</span>
          <h1 className="text-4xl sm:text-6xl font-bold tracking-tight text-white mt-3 font-heading">
            Ecosystem Applications.
          </h1>
          <p className="mt-4 text-base text-[#86868b] leading-relaxed">
            High-performance consumer software, mobile applications, and telemetry engines engineered by QuivixDigital.
          </p>
        </div>

        {/* Filter & Search Toolbar */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-12 pb-6 border-b border-white/[0.08]">
          <div className="relative w-full sm:w-80">
            <Search className="w-4 h-4 text-[#86868b] absolute left-3.5 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search applications..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-white/[0.04] border border-white/10 rounded-full pl-10 pr-4 py-2 text-xs text-white placeholder-[#86868b] focus:outline-none focus:border-blue-500 transition-all"
            />
          </div>

          <div className="flex items-center gap-2 self-start sm:self-auto">
            <span className="text-xs text-[#86868b] font-mono">Total Apps: {filteredApps.length}</span>
          </div>
        </div>

        {/* Grid Display */}
        {loading ? (
          <div className="py-24 flex flex-col items-center justify-center">
            <div className="loading-spinner"></div>
            <p className="text-xs font-mono text-[#86868b] mt-4">Loading QuivixDigital Ecosystem Specs...</p>
          </div>
        ) : filteredApps.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredApps.map((app) => (
              <Link
                key={app.id}
                to={`/apps/${app.id}`}
                className="group relative apple-glass rounded-3xl p-8 border border-white/[0.08] apple-card-hover flex flex-col justify-between overflow-hidden"
              >
                {/* Background Ambient Glow */}
                <div className="absolute top-0 right-0 w-36 h-36 bg-blue-500/10 rounded-full blur-2xl group-hover:bg-blue-500/20 transition-all duration-500" />

                <div className="relative z-10">
                  {/* App Icon Container */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="w-16 h-16 rounded-2xl bg-neutral-900 border border-white/15 overflow-hidden flex items-center justify-center shrink-0 shadow-lg group-hover:scale-105 group-hover:border-blue-400 transition-all duration-300">
                      <img src={getAppImage(app)} alt={app.title} className="w-full h-full object-cover" />
                    </div>

                    <span className="text-[10px] font-mono text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2.5 py-1 rounded-full">
                      20-30ms Latency
                    </span>
                  </div>

                  <span className="text-[10px] font-mono text-blue-400 uppercase tracking-widest bg-blue-500/10 px-2.5 py-0.5 rounded-full border border-blue-500/20">
                    {app.category || "QuivixDigital Software"}
                  </span>

                  <h2 className="text-xl font-bold text-white mt-3 group-hover:text-blue-400 transition-colors font-heading">
                    {app.title}
                  </h2>
                  <p className="text-xs text-[#86868b] mt-2 leading-relaxed line-clamp-3">
                    {app.description}
                  </p>
                </div>

                <div className="mt-8 pt-4 border-t border-white/[0.06] flex items-center justify-between text-xs text-white/70 group-hover:text-white relative z-10">
                  <span>Explore Specs & Features</span>
                  <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform text-blue-400" />
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="py-24 text-center apple-glass rounded-3xl border border-white/10 p-12">
            <Layers className="w-12 h-12 text-[#86868b] mx-auto mb-4" />
            <h3 className="text-lg font-bold text-white font-heading">No Applications Found</h3>
            <p className="text-xs text-[#86868b] mt-1">Try adjusting your search filters or check back later.</p>
          </div>
        )}

      </div>
    </div>
  );
};

export default AppsPage;
